/**
 * CyberSells Stats & Leveling Module
 * Handles player stats, XP, and leveling calculations
 */

(function() {
  'use strict';

  // XP thresholds for each level (exponential growth)
  const XP_CURVE = [
    0,      // Level 1
    100,    // Level 2
    250,    // Level 3
    500,    // Level 4
    850,    // Level 5
    1300,   // Level 6
    1850,   // Level 7
    2500,   // Level 8
    3250,   // Level 9
    4100,   // Level 10
    5050,   // Level 11
    6100,   // Level 12
    7250,   // Level 13
    8500,   // Level 14
    9850,   // Level 15
    11300,  // Level 16
    12850,  // Level 17
    14500,  // Level 18
    16250,  // Level 19
    18100,  // Level 20
  ];

  const MAX_LEVEL = XP_CURVE.length;

  /**
   * Calculate XP required for next level
   */
  function getXpForLevel(level) {
    if (level >= MAX_LEVEL) return XP_CURVE[MAX_LEVEL - 1];
    return XP_CURVE[level] || 0;
  }

  /**
   * Calculate current level from total XP
   */
  function calculateLevelFromXp(totalXp) {
    for (let level = MAX_LEVEL - 1; level >= 1; level--) {
      if (totalXp >= XP_CURVE[level]) {
        return level + 1;
      }
    }
    return 1;
  }

  /**
   * Get XP progress for current level (0-1)
   */
  function getLevelProgress(playerData) {
    const currentLevel = playerData.level;
    const currentXp = playerData.xp;
    
    if (currentLevel >= MAX_LEVEL) return 1;
    
    const currentLevelXp = getXpForLevel(currentLevel - 1);
    const nextLevelXp = getXpForLevel(currentLevel);
    const xpInLevel = currentXp - currentLevelXp;
    const xpNeeded = nextLevelXp - currentLevelXp;
    
    return Math.min(1, Math.max(0, xpInLevel / xpNeeded));
  }

  /**
   * Award XP and check for level up
   * @returns {Object} { leveledUp: boolean, newLevel: number, xpGained: number }
   */
  function awardXp(playerData, xpAmount) {
    const oldLevel = playerData.level;
    const oldXp = playerData.xp;
    
    playerData.xp += xpAmount;
    playerData.totalXpEarned += xpAmount;
    
    const newLevel = calculateLevelFromXp(playerData.xp);
    const leveledUp = newLevel > oldLevel;
    
    if (leveledUp) {
      playerData.level = newLevel;
    }
    
    return {
      leveledUp,
      oldLevel,
      newLevel,
      xpGained: xpAmount,
      totalXp: playerData.xp
    };
  }

  /**
   * Calculate XP from purchase amount
   * Formula: $1 = 10 XP base + bonus for large purchases
   */
  function calculatePurchaseXp(amountInCents) {
    const dollars = amountInCents / 100;
    let xp = Math.floor(dollars * 10);
    
    // Bonus XP for larger purchases
    if (dollars >= 100) xp += 200;
    else if (dollars >= 50) xp += 100;
    else if (dollars >= 25) xp += 50;
    
    return xp;
  }

  /**
   * Setup stat projection in cart
   * Shows how stats will change when items are purchased
   */
  function setupStatProjection(ctx) {
    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Stat projection module loaded');
    }
    
    // Find cart items and calculate stat changes
    const updateCartProjection = () => {
      // Safe no-op if DOM not available
      if (!document.querySelectorAll) return;
      
      const cartItems = document.querySelectorAll('[data-cart-item]');
      let statDeltas = {};
      
      cartItems.forEach(item => {
        // Parse stat modifiers from item data attributes
        const statsData = item.dataset.itemStats;
        if (statsData) {
          try {
            const stats = JSON.parse(statsData);
            for (const [stat, value] of Object.entries(stats)) {
              statDeltas[stat] = (statDeltas[stat] || 0) + value;
            }
          } catch (e) {
            if (window.cyberSellsConfig?.debug) {
              console.warn('[CyberSells] Invalid stat data:', e);
            }
          }
        }
      });
      
      // Display projected stats
      displayStatProjection(statDeltas);
    };
    
    // Listen for cart changes
    document.addEventListener('cart:updated', updateCartProjection);
    
    // Initial projection
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateCartProjection);
    } else {
      updateCartProjection();
    }
    
    return { updateCartProjection };
  }

  /**
   * Display stat projection in UI
   */
  function displayStatProjection(statDeltas) {
    // Safe no-op if container not present
    const projectionContainer = document.querySelector('[data-stat-projection]');
    if (!projectionContainer) return;
    
    let html = '<div class="stat-projection">';
    html += '<h3 class="stat-projection__title">Power-Up Preview</h3>';
    html += '<div class="stat-projection__stats">';
    
    for (const [stat, delta] of Object.entries(statDeltas)) {
      if (delta !== 0) {
        const sign = delta > 0 ? '+' : '';
        const className = delta > 0 ? 'positive' : 'negative';
        html += `<div class="stat-projection__item stat-projection__item--${className}">`;
        html += `<span class="stat-projection__name">${stat.toUpperCase()}</span>`;
        html += `<span class="stat-projection__value">${sign}${delta}</span>`;
        html += `</div>`;
      }
    }
    
    html += '</div></div>';
    projectionContainer.innerHTML = html;
  }

  /**
   * Fetch player stats (async wrapper for player data)
   */
  async function fetchPlayerStats(ctx, params = {}) {
    if (!window.CyberSells_playerData) {
      return {
        success: false,
        error: 'Player data module not loaded',
        data: null
      };
    }

    try {
      const playerData = window.CyberSells_playerData.loadPlayerData();
      
      return {
        success: true,
        data: {
          stats: playerData.stats,
          level: playerData.level,
          xp: playerData.xp,
          progress: getLevelProgress(playerData),
          xpForNextLevel: getXpForLevel(playerData.level),
          achievements: playerData.achievements || []
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // Expose module functions
  window.CyberSells_stats = {
    setupStatProjection,
    fetchPlayerStats,
    awardXp,
    calculatePurchaseXp,
    calculateLevelFromXp,
    getLevelProgress,
    getXpForLevel,
    MAX_LEVEL
  };

})();
